import React, { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode } from 'react';

export type ChartPngPageId =
  | 'overview'
  | 'transportation'
  | 'buildings'
  | 'waste'
  | 'people'
  | 'statewide_analysis';

export const ChartPngPageIdOverrideContext = createContext<ChartPngPageId | undefined>(undefined);

export interface ChartPngEntry {
  id: string;
  label: string;
  pageId?: ChartPngPageId;
}

interface ChartPngRegistryActionsType {
  register: (id: string, label: string, exportPng: () => void, pageId?: ChartPngPageId) => void;
  unregister: (id: string) => void;
  triggerExport: (ids: string[]) => void;
}

const ChartPngRegistryActionsContext = createContext<ChartPngRegistryActionsType | undefined>(undefined);
const ChartPngRegistryEntriesContext = createContext<ChartPngEntry[]>([]);

export function ChartPngRegistryProvider({ children }: { children: ReactNode }) {
  const chartsRef = useRef<Map<string, { label: string; exportPng: () => void; pageId?: ChartPngPageId }>>(new Map());
  const flushScheduledRef = useRef(false);
  const [entries, setEntries] = useState<ChartPngEntry[]>([]);

  const flushEntries = useCallback(() => {
    const next = Array.from(chartsRef.current.entries()).map(([i, v]) => ({ id: i, label: v.label, pageId: v.pageId }));
    setEntries((prev) => {
      if (prev.length !== next.length || next.some((e, i) => prev[i]?.id !== e.id || prev[i]?.label !== e.label || prev[i]?.pageId !== e.pageId)) return next;
      return prev;
    });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushScheduledRef.current) return;
    flushScheduledRef.current = true;
    requestAnimationFrame(() => {
      flushScheduledRef.current = false;
      flushEntries();
    });
  }, [flushEntries]);

  const register = useCallback((id: string, label: string, exportPng: () => void, pageId?: ChartPngPageId) => {
    chartsRef.current.set(id, { label, exportPng, pageId });
    scheduleFlush();
  }, [scheduleFlush]);

  const unregister = useCallback((id: string) => {
    chartsRef.current.delete(id);
    scheduleFlush();
  }, [scheduleFlush]);

  const triggerExport = useCallback((ids: string[]) => {
    ids.forEach((id) => chartsRef.current.get(id)?.exportPng());
  }, []);

  const actions = useMemo(() => ({ register, unregister, triggerExport }), [register, unregister, triggerExport]);

  return (
    <ChartPngRegistryActionsContext.Provider value={actions}>
      <ChartPngRegistryEntriesContext.Provider value={entries}>
        {children}
      </ChartPngRegistryEntriesContext.Provider>
    </ChartPngRegistryActionsContext.Provider>
  );
}

/** Use only when you need entries (e.g. download modal). Does not re-render when only entries change if you use useChartPngRegistryActions for registration. */
export function useChartPngRegistry() {
  const actions = useContext(ChartPngRegistryActionsContext);
  const entries = useContext(ChartPngRegistryEntriesContext);
  if (actions === undefined) throw new Error('useChartPngRegistry must be used within ChartPngRegistryProvider');
  return { ...actions, entries };
}

/** Stable actions only. Use in charts/pages that register/unregister so they do not re-render when entries list changes. */
export function useChartPngRegistryActions() {
  const ctx = useContext(ChartPngRegistryActionsContext);
  if (ctx === undefined) throw new Error('useChartPngRegistryActions must be used within ChartPngRegistryProvider');
  return ctx;
}

/** Stable id from chart title for registry (alphanumeric + underscore). */
export function chartPngIdFromTitle(title: string): string {
  return (title || 'chart').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') || 'chart';
}
